/**
 * Two-Factor Authentication Page
 * 
 * Page displayed after login when 2FA is enabled.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, KeyRound, ArrowLeft, Loader2, CheckCircle2, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';

export default function TwoFactorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verify2FA } = useAuth();
  
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  
  // Get the temporary token from navigation state
  const tempToken = location.state?.tempToken;
  const email = location.state?.email;
  // "Manter Conectado" escolhido na tela de login, propagado via router state.
  const rememberMe = location.state?.rememberMe ?? false;
  
  // Redirect to login if no temp token
  useEffect(() => {
    if (!tempToken) {
      toast({
        title: 'Sessão expirada',
        description: 'Por favor, faça login novamente.',
        variant: 'destructive',
      });
      navigate('/login');
    }
  }, [tempToken, navigate]);
  
  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Digite o código completo de 6 dígitos');
      return;
    }
    
    setError('');
    setIsVerifying(true);
    
    try {
      // Call the 2FA verification API
      if (verify2FA) {
        await verify2FA(tempToken, code, rememberMe);
        toast({ title: 'Autenticação bem-sucedida!' });
        navigate('/financial');
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Código inválido. Tente novamente.');
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleBackToLogin = () => {
    navigate('/login');
  };
  
  const securityFeatures = [
    {
      icon: Shield,
      title: 'Proteção Extra',
      description: 'Sua conta está protegida com autenticação de dois fatores.',
    },
    {
      icon: Smartphone,
      title: 'App Autenticador',
      description: 'Use o código gerado pelo seu app autenticador.',
    },
    {
      icon: CheckCircle2,
      title: 'Acesso Seguro',
      description: 'Apenas você pode acessar sua conta com este código.',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Hero */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden" style={{ background: BRAND_HERO_GRADIENT }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#F5B322]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-10 w-48 h-48 bg-[#F5B322]/10 rounded-full blur-3xl" />
        
        {/* Logo */}
        <div className="relative z-10">
          <img
            src="/logoSidebar.png"
            alt="Librattum"
            className="h-10"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#F5B322]/20 mb-4">
              <Shield className="h-10 w-10 text-[#F5B322]" />
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight">
              Verificação de<br />
              <span className="text-[#F5B322]">Segurança</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-md">
              Sua conta está protegida com autenticação de dois fatores.
              Digite o código do seu aplicativo autenticador.
            </p>
          </div>
          
          {/* Security Features */}
          <div className="space-y-4">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-[#F5B322]/20">
                  <feature.icon className="h-5 w-5 text-[#F5B322]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-gray-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="relative z-10 text-gray-500 text-sm">
          © {new Date().getFullYear()} Librattum. Todos os direitos reservados.
        </div>
      </div>
      
      {/* Right Section - 2FA Form */}
      <div className="flex-1 flex items-center justify-center p-8 brand-light-surface">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo-librattum.png"
              alt="Librattum"
              className="h-10 mx-auto"
            />
          </div>
          
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#F5B322]/20 mb-6">
              <KeyRound className="h-10 w-10 text-[#A1700F]" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Verificação 2FA
            </h2>
            <p className="mt-2 text-gray-600">
              Digite o código de 6 dígitos do seu aplicativo autenticador
            </p>
            {email && (
              <p className="mt-1 text-sm text-gray-500">
                Conta: <span className="font-medium">{email}</span>
              </p>
            )}
          </div>
          
          {/* OTP Input */}
          <div className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => {
                  setCode(value);
                  setError('');
                }}
                onComplete={handleVerify}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                  <InputOTPSlot index={1} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                  <InputOTPSlot index={2} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                  <InputOTPSlot index={4} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                  <InputOTPSlot index={5} className="w-14 h-14 text-2xl text-gray-900 bg-white border-gray-300 ring-[#F5B322]" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            
            {error && (
              <p className="text-center text-sm text-red-600">{error}</p>
            )}
            
            <Button
              onClick={handleVerify}
              disabled={isVerifying || code.length !== 6}
              className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
            >
              {isVerifying ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verificando...
                </div>
              ) : (
                'Verificar e Entrar'
              )}
            </Button>
          </div>
          
          {/* Help Text */}
          <div className="space-y-4">
            <div className="bg-gray-100 rounded-xl p-4 text-sm text-gray-600">
              <p className="font-medium text-gray-800 mb-2">Não tem acesso ao código?</p>
              <ul className="space-y-1 text-gray-500">
                <li>• Verifique se o horário do seu dispositivo está correto</li>
                <li>• Use um código de recuperação, se disponível</li>
                <li>• Entre em contato com o suporte para ajuda</li>
              </ul>
            </div>
            
            <button
              onClick={handleBackToLogin}
              className="flex items-center justify-center gap-2 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar ao login
            </button>
          </div>
          
          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 pt-4 text-sm text-gray-500">
            <div className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-[#F5B322]" />
              Conexão segura
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
              Dados protegidos
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

